import React, { useContext } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { ShieldOff } from 'lucide-react'
import { AuthContext } from '../../../context/AuthContext'
import useOrgModules from '../../../hooks/useOrgModules'
import StepUpGate, {
  rememberCasesDestination,
} from '../../cases/StepUpGate'
import { CasesSurface } from '../../cases/Shell'
import { Empty } from '../../cases/ui'

// ======================================================================
// THE GUARD FOR THE ER MODULE'S TENANT-ADMIN SCREENS
// ======================================================================
//
// Three gates, and one deliberate omission:
//
//   1. IS THERE A KWERY SESSION?        No -> /login.
//   2. IS THIS A TENANT ADMIN?          No -> the same neutral screen a
//                                       non-admin gets anywhere else.
//   3. IS THE ER MODULE ON FOR THE ORG? No -> the same screen.
//   4. STEP-UP                          ONLY when `stepUp` is set. The
//                                       case list sets it; case type
//                                       settings must not.
//
// ======================================================================
// WHY THIS IS NOT routes/RoleRoute.jsx, AND IT IS NOT A STYLE CHOICE
// ======================================================================
// RoleRoute gates every other /admin route in the application, and it
// does two things that are wrong for THIS surface:
//
//   (a) IT BYPASSES SUPERADMIN — `if (user.role === "superadmin")
//       return children`. That is right for platform administration and
//       wrong here. These screens are a TENANT surface: one shows every
//       disciplinary case in a customer's organisation. Kwery's own
//       operational accounts have no business on it, and the backend
//       says so — er_cases/case_types.require_tenant_case_admin()
//       refuses superadmin FIRST and SEPARATELY, before any role test.
//       Routing a superadmin here would render a screen that then 403s
//       on every request: a dead end that looks like a bug and reads
//       like a permission they nearly have.
//
//   (b) IT READS user.role ONLY, ignoring the `roles` CSV. The backend
//       treats the two as a UNION — ai_agent.rbac.user_role_set() merges
//       the `role` column with the comma-separated `roles` column,
//       because the frontend has always treated them that way, and
//       Sidebar.jsx's own isAdmin predicate does too. So somebody with
//       role='staff' and roles='admin' is an admin everywhere in this
//       application and to the API, and would be redirected to /403 by
//       RoleRoute alone. Its own comment concedes the gap.
//
// This guard therefore mirrors the BACKEND's rule exactly: the union of
// role and roles, admin only, superadmin refused. RoleRoute is left
// alone — it is used by seventy other routes and is not this task's to
// change — and the divergence is stated here rather than worked around.
//
// EXECUTIVE IS REFUSED TOO. Task 23 narrowed the backend gate to exact
// membership of {"admin"} after finding that rbac.user_has_any_role()
// applies ROLE_HIERARCHY and was quietly admitting executives. This
// mirrors that: an executive holding a list of every disciplinary case
// in the company is not something to arrive at by hierarchy.
//
// ======================================================================
// THE REFUSAL SCREEN SAYS NOTHING ABOUT WHAT IS BEHIND IT
// ======================================================================
// It does not say how many cases there are, whether the module is
// configured, or whether the person is one role short. A screen that
// distinguished "not an admin" from "module off" would answer a
// question about the organisation to somebody who is not entitled to
// ask it. One screen, one sentence, and the route is not the place
// anybody learns anything.

/** The same predicate ai_agent.rbac.user_role_set() applies server-side:
 *  the union of the primary `role` column and the comma-separated
 *  `roles` column, lowercased.
 *
 *  EXPORTED, because the dashboard card must decide whether to draw
 *  itself with the identical rule. A card that appears for somebody the
 *  route then refuses is worse than no card. */
export function readAdminRoles() {
  let user = null
  try {
    user = JSON.parse(localStorage.getItem('user') || 'null')
  } catch {
    /* corrupt storage reads as no session */
  }
  if (!user) return null

  const held = new Set()
  if (user.role) held.add(String(user.role).trim().toLowerCase())
  const secondary = user.roles
  if (typeof secondary === 'string') {
    secondary.split(',').forEach((r) => {
      const v = r.trim().toLowerCase()
      if (v) held.add(v)
    })
  } else if (Array.isArray(secondary)) {
    secondary.forEach((r) => {
      const v = String(r || '').trim().toLowerCase()
      if (v) held.add(v)
    })
  }
  // is_admin is a boolean the rest of this application already honours
  // (Sidebar.jsx, AdminDashboardPage.jsx). It is folded in as 'admin'
  // so this predicate cannot be STRICTER than the surrounding app —
  // being refused a screen the sidebar offered is its own defect.
  if (user.is_admin) held.add('admin')
  return held
}

/** True for a tenant admin. False for superadmin, executive, manager,
 *  staff, and anybody not logged in. */
export function isTenantCaseAdmin() {
  const held = readAdminRoles()
  if (!held) return false
  // FIRST AND SEPARATELY, exactly as the backend does it, and for the
  // same reason: it must stay correct if the test below is ever
  // loosened to something hierarchy-aware.
  if (held.has('superadmin')) return false
  return held.has('admin')
}

/** True unless the org explicitly has er_cases off.
 *
 *  READ FROM `modules` RATHER THAN THROUGH isEnabled(), for the reason
 *  Sidebar.jsx's useShowCasesEntry gives: isEnabled() is FAIL-OPEN by
 *  design so the UI does not blank out while loading, and that default
 *  is wrong for a module that is off by default. An explicit false
 *  hides the screen; anything else falls through to the API, which is
 *  the real gate either way. */
export function useErModuleOn() {
  const { modules } = useOrgModules()
  return modules?.er_cases !== false
}

function Refused() {
  return (
    <CasesSurface>
      <div className="mx-auto w-full max-w-3xl px-5 py-16 sm:px-8">
        <div className="panel">
          <Empty icon={ShieldOff} title="This area is not available to you">
            Case administration is limited to organisation administrators.
          </Empty>
        </div>
      </div>
    </CasesSurface>
  )
}

export default function AdminCasesRoute({ children, stepUp = false }) {
  const { isAuthenticated, loading, silentRefresh } = useContext(AuthContext)
  const location = useLocation()
  const navigate = useNavigate()
  const moduleOn = useErModuleOn()

  const here = `${location.pathname}${location.search}`

  if (!loading && !isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (loading) {
    // The same neutral line ProtectedRoute and CasesRoute both use, so
    // this route does not flash something different on every load.
    return <div className="text-[#00FF99] p-8 text-center">Loading…</div>
  }

  if (!isTenantCaseAdmin() || !moduleOn) {
    return <Refused />
  }

  if (!stepUp) return children

  return (
    <StepUpGate
      silentRefresh={silentRefresh}
      onSignedOut={() => {
        // The deep-link stash only accepts /cases paths, so an admin
        // sent back to log in returns to the app rather than here. That
        // is correct and not a limitation worth widening: this screen
        // is reached from the admin dashboard, which is two clicks from
        // anywhere, and widening the stash would widen what a stale
        // sessionStorage value can redirect to.
        rememberCasesDestination(here)
        navigate('/login', { replace: true })
      }}
    >
      {children}
    </StepUpGate>
  )
}
